import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Avatar,
  InputAdornment,
  TextField,
  Chip,
  Tooltip,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import EditIcon from "@material-ui/icons/Edit";
import GroupIcon from "@material-ui/icons/Group";
import SearchIcon from "@material-ui/icons/Search";
import SecurityIcon from "@material-ui/icons/Security";
import BlockIcon from "@material-ui/icons/Block";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: theme.spacing(1),
  },
  backButton: {
    marginRight: theme.spacing(0.5),
  },
  adminHintChip: {
    marginLeft: "auto",
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255, 167, 38, 0.15)"
      : "rgba(237, 108, 2, 0.08)",
    color: theme.palette.type === "dark"
      ? theme.palette.warning.light
      : theme.palette.warning.dark,
    fontWeight: 500,
    fontSize: "0.75rem",
    height: 28,
    "& .MuiChip-icon": {
      color: "inherit",
      fontSize: 16,
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  searchField: {
    flex: 1,
    minWidth: 220,
    maxWidth: 400,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
    },
  },
  resultCount: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
  },
  tableContainer: {
    borderRadius: 8,
    boxShadow: theme.palette.type === "dark"
      ? "0 1px 4px rgba(0,0,0,0.4)"
      : "0 1px 4px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  table: {
    minWidth: 500,
  },
  tableHead: {
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.04)"
      : theme.palette.grey[50],
    "& .MuiTableCell-head": {
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5, 2),
    },
  },
  tableRow: {
    transition: "background-color 150ms ease",
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.02)"
        : "rgba(0,0,0,0.015)",
    },
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
    },
    "& .MuiTableCell-body": {
      padding: theme.spacing(1.25, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  avatarCell: {
    width: 64,
  },
  avatar: {
    width: 40,
    height: 40,
    backgroundColor: theme.palette.primary.main,
    fontSize: "1rem",
  },
  groupNameText: {
    fontWeight: 500,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  participantsChip: {
    height: 24,
    fontSize: "0.78rem",
    fontWeight: 500,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(144, 202, 249, 0.12)"
      : "rgba(33, 150, 243, 0.08)",
    color: theme.palette.type === "dark"
      ? theme.palette.info.light
      : theme.palette.info.dark,
    "& .MuiChip-icon": {
      color: "inherit",
      fontSize: 14,
    },
  },
  editButton: {
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.8rem",
    padding: theme.spacing(0.5, 1.5),
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8),
    gap: theme.spacing(2),
  },
  loadingText: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 4),
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: 64,
    color: theme.palette.action.disabled,
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    fontWeight: 600,
    fontSize: "1.1rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  emptySubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    maxWidth: 360,
    lineHeight: 1.5,
  },
}));

const GroupEdit = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!connectionId) {
      history.replace("/group-management");
      return;
    }
    const fetchGroups = async () => {
      try {
        const { data } = await api.get(
          `/group-management/connections/${connectionId}/groups`,
          { params: { adminOnly: "true" } }
        );
        setGroups(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [connectionId, history]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groups;
    const term = searchTerm.toLowerCase().trim();
    return groups.filter(
      (g) =>
        (g.subject || g.id || "").toLowerCase().includes(term)
    );
  }, [groups, searchTerm]);

  const handleBack = () => history.push("/group-management");

  const handleOpenGroup = (group) => {
    history.push(
      `/group-management/groups/${encodeURIComponent(group.id)}/edit?connectionId=${connectionId}`
    );
  };

  if (!connectionId) return null;

  return (
    <MainContainer>
      <MainHeader>
        <div className={classes.headerContent}>
          <Button
            className={classes.backButton}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            {i18n.t("groupManagement.back")}
          </Button>
          <Title>{i18n.t("groupManagement.editGroup")}</Title>
          <Chip
            icon={<SecurityIcon />}
            label={i18n.t("groupManagement.editGroupAdminOnlyHint")}
            className={classes.adminHintChip}
            size="small"
            variant="outlined"
          />
        </div>
      </MainHeader>

      <Box className={classes.root}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={40} />
            <Typography className={classes.loadingText}>
              {i18n.t("groupManagement.editGroupAdminOnlyHint")}
            </Typography>
          </Box>
        ) : groups.length === 0 ? (
          <Paper className={classes.tableContainer}>
            <Box className={classes.emptyState}>
              <BlockIcon className={classes.emptyIcon} />
              <Typography className={classes.emptyTitle}>
                {i18n.t("groupManagement.noAdminGroups")}
              </Typography>
              <Typography className={classes.emptySubtitle}>
                {i18n.t("groupManagement.editGroupAdminOnlyHint")}
              </Typography>
            </Box>
          </Paper>
        ) : (
          <>
            <Box className={classes.toolbar}>
              <TextField
                className={classes.searchField}
                variant="outlined"
                size="small"
                placeholder={i18n.t("groupManagement.groupName")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography className={classes.resultCount}>
                {filteredGroups.length} / {groups.length}
              </Typography>
            </Box>

            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table className={classes.table} size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.avatarCell} />
                    <TableCell>
                      {i18n.t("groupManagement.groupName")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("groupManagement.participantsCount")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("groupManagement.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Box className={classes.emptyState} style={{ padding: 32 }}>
                          <SearchIcon
                            className={classes.emptyIcon}
                            style={{ fontSize: 48 }}
                          />
                          <Typography className={classes.emptyTitle}>
                            {i18n.t("groupManagement.noAdminGroups")}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGroups.map((group) => (
                      <TableRow key={group.id} className={classes.tableRow}>
                        <TableCell className={classes.avatarCell}>
                          <Avatar
                            src={group.pictureUrl}
                            className={classes.avatar}
                            variant="circular"
                          >
                            <GroupIcon />
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.groupNameText}>
                            {group.subject || group.id}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<GroupIcon />}
                            label={group.participantsCount ?? "-"}
                            className={classes.participantsChip}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={i18n.t("groupManagement.editGroup")}
                            arrow
                          >
                            <Button
                              className={classes.editButton}
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenGroup(group)}
                            >
                              {i18n.t("groupManagement.editGroup")}
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </MainContainer>
  );
};

export default GroupEdit;
